import React, { useEffect, useState } from 'react';
import { Avatar, Flex, Select } from 'antd';
import agbColors from 'constants/Colors/agbColors';
import agbPlatformText from 'constants/platformLabels/agbPlatformText';
import { useLanguage } from 'hooks/Language/useLanguage';
import { ChevronDown, Building2, CheckCircle2 } from 'lucide-react';
import { CURRENT_PLATFORM, Platform } from 'utils/config';
import { IFormattedAccountNumber } from 'interfaces/components/pageProps';
import { IDisplayAccountNumbers } from 'interfaces/queries/transactions';
import './index.scss';

const { Option } = Select;

interface SelectOption {
  key: number;
  text: string;
  value: string;
  balance: string;
  icon: string;
  default?: string;
  linked?: string;
}

const accountLogo = CURRENT_PLATFORM === Platform.AGB;

const AccountDropdown: React.FC<{
  account?: any;
  loading?: boolean;
  options?: any[];
  setAccount?: any;
  showBalance?: boolean;
  size?: string;
  sizePadding?: any;
}> = ({
  account,
  loading,
  options,
  setAccount,
  showBalance = false,
  size = '250px',
  sizePadding = 'large',
}) => {
  const { translate } = useLanguage();
  const [imageErrors, setImageErrors] = useState<
    Record<string, boolean>
  >({});

  const onChange = (value: any) => {
    const selected = options?.find(
      (option: any) => option.value === value?.value,
    );
    setAccount({
      value: value.value,
      label: selected
        ? renderSelectValue(selected, showBalance, imageErrors)
        : undefined,
      currency: selected?.currency,
    });
  };

  const defaultAccountNumber = () => {
    const selected = options
      ? options?.find(
          (item) => item?.linked == 'YES' || item?.default === 'YES',
        )
      : null;
    return {
      value: selected?.value,
      label:
        renderSelectValue(selected, showBalance, imageErrors) ||
        undefined,
      currency: selected?.currency,
    };
  };

  useEffect(() => {
    if (!loading && options?.length) {
      setAccount(defaultAccountNumber());
    }
  }, [loading]);

  const handleImageError = (accountKey: string) => {
    setImageErrors((prev) => ({ ...prev, [accountKey]: true }));
  };

  const accountInformationData =
    account || defaultAccountNumber() || {};

  return (
    <Select
      labelInValue
      size="large"
      value={
        account?.value || defaultAccountNumber()?.value
          ? accountInformationData
          : undefined
      }
      placeholder={translate('Select account number')}
      loading={loading}
      onChange={onChange}
      optionLabelProp="value"
      suffixIcon={<ChevronDown size={20} strokeWidth={2.5} />}
      getPopupContainer={(trigger) =>
        trigger.parentElement || document.body
      }
      virtual={true}
      listHeight={400}
      transitionName="ant-slide-up"
      style={{
        width: size,
        minHeight: '56px',
      }}
      dropdownStyle={{
        borderRadius: '12px',
        boxShadow:
          '0 8px 32px rgba(0, 0, 0, 0.1), 0 2px 8px rgba(0, 0, 0, 0.06)',
        padding: '6px',
        border: '1px solid #e5e7eb',
      }}
      className="modern-account-select"
    >
      {options &&
        options.map((option: any) =>
          renderOption(
            option,
            showBalance,
            imageErrors,
            handleImageError,
          ),
        )}
    </Select>
  );
};

export const renderOption = (
  option: SelectOption,
  showBalance?: boolean,
  imageErrors?: Record<string, boolean>,
  onImageError?: (key: string) => void,
) => {
  const hasError = imageErrors?.[option.key];
  const isDefault =
    option.default === 'YES' || option.linked === 'YES';

  return (
    <Option
      key={option.key}
      value={option.value}
      style={{ padding: '0px' }}
    >
      <Flex
        gap="middle"
        align="center"
        className="modern-account-option"
      >
        <div className="account-avatar-wrapper">
          {!hasError && option.icon ? (
            <Avatar
              src={option.icon}
              onError={() => {
                onImageError?.(String(option.key));
                return true;
              }}
              style={{
                backgroundColor: agbColors.whiteColor,
                border: '2px solid #e5e7eb',
              }}
              size={44}
            />
          ) : (
            <Avatar
              icon={<Building2 size={20} />}
              style={{
                backgroundColor: '#f0f7ff',
                color: agbColors.navyBlueColor,
                border: '2px solid #e6f3ff',
              }}
              size={44}
            />
          )}
        </div>

        <Flex vertical gap={4} style={{ flex: 1 }}>
          <Flex align="center" gap={8}>
            <span className="account-name">{option.text}</span>
            {isDefault && (
              <span className="default-badge">
                <CheckCircle2 size={14} strokeWidth={2.5} />
                Default
              </span>
            )}
          </Flex>
          <Flex align="center" gap={8}>
            <span className="account-number">{option.value}</span>
            {showBalance && (
              <span className="account-balance">
                {option.balance}
              </span>
            )}
          </Flex>
        </Flex>
      </Flex>
    </Option>
  );
};

export const renderSelectValue = (
  option: SelectOption,
  showBalance?: boolean,
  imageErrors?: Record<string, boolean>,
) => {
  if (!option?.value) return null;

  const hasError = imageErrors?.[option.key];

  return (
    <Flex align="center" gap={12} className="selected-account-value">
      <div className="selected-avatar-wrapper">
        {!hasError && option.icon ? (
          <Avatar
            src={option.icon}
            style={{
              backgroundColor: '#ffffff',
              border: 'none',
            }}
            size={36}
          />
        ) : (
          <Avatar
            icon={<Building2 size={16} strokeWidth={2} />}
            style={{
              backgroundColor: '#2b3a67',
              color: '#ffffff',
              border: 'none',
            }}
            size={36}
          />
        )}
      </div>

      <Flex vertical gap={2} style={{ flex: 1, minWidth: 0 }}>
        <span className="selected-account-name">{option?.text}</span>
        <span className="selected-account-number">
          {option?.value}
        </span>
      </Flex>

      {showBalance && (
        <span className="selected-balance">{option?.balance}</span>
      )}
    </Flex>
  );
};

export const formattedAccountList = (
  accountData: any[] | undefined,
): IFormattedAccountNumber[] =>
  accountData?.map((item: IDisplayAccountNumbers, index: any) => ({
    key: index,
    text: item.AccountName,
    value: item.AccountNumber,
    balance: `${item.CurrencyCode} ${item.Balance}`,
    icon: !accountLogo
      ? item.Flag || item.Operator?.Logo
      : agbPlatformText.icon,
    default: item.Default,
    linked: item.Linked,
    currency: item.Currency || item.CurrencyCode,
    operatorID: item?.BankCode,
    brandUnikID: item?.BrandUnikID,
  })) || [];

export default AccountDropdown;
